/**
 * MOB search patterns as leg-by-leg instructions, the way a helm actually flies
 * them: a heading to steer, a speed to hold, and a time to run before the turn.
 *
 * Edd's constraint: we do NOT draw the sector search or the expanding square on
 * the chart. On a small screen in a real emergency, a helm can't be reading a
 * plotted spiral. What they can do is steer a compass heading, hold a speed, and
 * turn when a timer runs out. So each pattern reduces to a table of legs, and the
 * UI counts each leg down to the turn.
 *
 * Two important reminders carried on every leg, straight off the RNLI cards:
 *   - steer a COMPASS HEADING, not the COG the plotter shows. Tide and leeway
 *     bend the ground track; the pattern is built in headings, so steer the
 *     heading and let the set take the boat where it will.
 *   - hold a SPEED THROUGH THE WATER, not speed over the ground. The leg times
 *     assume the boat is making its through-water speed; a tide under it changes
 *     the SOG but not the time the leg should take.
 */

/** Compass points for a heading, for the "steer roughly NE" cue on each leg. */
const CARDINALS = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
];

/** Nearest 16-point cardinal for a heading in degrees. */
export function cardinalFor(headingDeg: number): string {
  const h = (((headingDeg % 360) + 360) % 360);
  return CARDINALS[Math.round(h / 22.5) % 16];
}

export type PatternId = 'expanding_square' | 'sector_search';

export interface SearchLeg {
  /** 1-based leg number. */
  legNumber: number;
  /** Compass heading to steer, degrees (0..359). */
  headingDeg: number;
  /** Nearest cardinal point, e.g. "NE". */
  cardinal: string;
  /** Leg length through the water, nm. */
  lengthNm: number;
  /** Speed through the water to hold, kn. */
  stwKn: number;
  /** Time to run this leg, seconds. */
  timeSec: number;
  /**
   * True when this leg ends back at (or crossing) the datum, where the helm
   * corrects course and restarts the stopwatch. Sector search only; always false
   * for the expanding square.
   */
  atDatum: boolean;
}

export interface SearchPattern {
  id: PatternId;
  legs: SearchLeg[];
  /** Short reminders to show above the table. */
  notes: string[];
  /**
   * True when the leg headings are NOT yet verified against the source card and
   * must not be presented as authoritative. Both patterns are now verified
   * (expanding square and sector search against RNLI Card 14), so this is false;
   * the field stays so any future unchecked pattern can flag itself.
   */
  headingsNeedVerification: boolean;
}

/** Seconds to run a leg of `lengthNm` at `stwKn` (0 speed -> Infinity, guarded). */
function legSeconds(lengthNm: number, stwKn: number): number {
  if (stwKn <= 0) return Infinity;
  return (lengthNm / stwKn) * 3600;
}

/**
 * Expanding square search.
 *
 * Fully specified by the RNLI card, so we build it exactly:
 *   - headings cycle N, E, S, W: heading = [000, 090, 180, 270][(leg-1) mod 4].
 *   - the legs grow in pairs: legs 1 & 2 are one track spacing, 3 & 4 are two,
 *     5 & 6 are three, and so on. length = ceil(leg / 2) x initial track spacing.
 *   - time per leg = length / speed.
 *
 * Verified against the card: initial track spacing 0.4 nm at 15 kt gives legs
 * 1&2 at 01:36, 3&4 at 03:12, 5&6 at 04:48, 7&8 at 06:24, 9&10 at 08:00.
 */
export function expandingSquareLegs(
  initialTrackSpacingNm: number,
  stwKn: number,
  legCount = 20,
): SearchPattern {
  const headings = [0, 90, 180, 270];
  const legs: SearchLeg[] = [];
  for (let leg = 1; leg <= legCount; leg++) {
    const heading = headings[(leg - 1) % 4];
    const multiplier = Math.ceil(leg / 2);
    const lengthNm = multiplier * initialTrackSpacingNm;
    legs.push({
      legNumber: leg,
      headingDeg: heading,
      cardinal: cardinalFor(heading),
      lengthNm,
      stwKn,
      timeSec: legSeconds(lengthNm, stwKn),
      atDatum: false,
    });
  }
  return {
    id: 'expanding_square',
    legs,
    notes: [
      'Steer the COMPASS HEADING shown, not the COG on the plotter.',
      'Hold the speed through the water (STW), not speed over the ground.',
      'The legs grow every second turn. Turn on the countdown, not by eye.',
      'Start the first leg from the datum, heading north.',
    ],
    headingsNeedVerification: false,
  };
}

/**
 * The sector-search first-nine leg headings, starting north (000) and built as
 * three equilateral triangles about the datum. Each triangle is three sides of
 * 120° that close back on the datum, and each triangle starts 240° on from the
 * last so the three together sweep the whole circle:
 *   triangle 1 (legs 1–3): 000, 120, 240
 *   triangle 2 (legs 4–6): 240, 000, 120
 *   triangle 3 (legs 7–9): 120, 240, 000
 * Leg index 0..8 -> ((2 * triangle + legInTriangle) * 120)°.
 *
 * Verified against RNLI ALB SAR Check Card 14 (Sector Search, water-based).
 */
export function sectorFirstNineHeadings(): number[] {
  const out: number[] = [];
  for (let i = 0; i < 9; i++) {
    const triangle = Math.floor(i / 3);
    const legInTriangle = i % 3;
    out.push(((2 * triangle + legInTriangle) * 120) % 360);
  }
  return out;
}

/**
 * Sector search.
 *
 * Built to RNLI ALB SAR Check Card 14: every leg is ONE track spacing long
 * ("leg length equal to one track spacing"), so every leg runs the same time;
 * 18 legs make two rotations of nine; the second rotation (legs 10–18) repeats
 * the first nine headings offset by 30°, to fill the gaps the first rotation
 * left; the first leg is northerly (000); the helm corrects for the datum and
 * restarts the stopwatch each time a leg brings the boat back to it (every third
 * leg ends on the datum). Where two consecutive legs share a heading (legs 3 & 4,
 * 6 & 7, and the equivalents in the second rotation) the boat runs straight
 * through the datum: they stay two separate one-track-spacing legs, not one
 * double leg.
 *
 * Verified against RNLI ALB SAR Check Card 14 (Sector Search, water-based), so
 * this returns with `headingsNeedVerification: false`.
 */
export function sectorSearchLegs(trackSpacingNm: number, stwKn: number): SearchPattern {
  const firstNine = sectorFirstNineHeadings();
  const legs: SearchLeg[] = [];
  for (let leg = 1; leg <= 18; leg++) {
    const idx = (leg - 1) % 9;
    const rotationOffset = leg <= 9 ? 0 : 30;
    const heading = (firstNine[idx] + rotationOffset) % 360;
    legs.push({
      legNumber: leg,
      headingDeg: heading,
      cardinal: cardinalFor(heading),
      lengthNm: trackSpacingNm,
      stwKn,
      timeSec: legSeconds(trackSpacingNm, stwKn),
      // Every third leg of a triangle brings the boat back to the datum.
      atDatum: leg % 3 === 0,
    });
  }
  return {
    id: 'sector_search',
    legs,
    notes: [
      'Sector search — RNLI ALB SAR Check Card 14.',
      'Steer the COMPASS HEADING shown, not the COG on the plotter.',
      'Hold the speed through the water (STW), not speed over the ground.',
      'Every third leg returns to the datum: correct for it and restart the timer.',
      'Second rotation (legs 10–18) repeats the first nine, offset 30°.',
    ],
    headingsNeedVerification: false,
  };
}

/** Build whichever pattern was chosen. */
export function buildPattern(
  id: PatternId,
  trackSpacingNm: number,
  stwKn: number,
): SearchPattern {
  return id === 'expanding_square'
    ? expandingSquareLegs(trackSpacingNm, stwKn)
    : sectorSearchLegs(trackSpacingNm, stwKn);
}

export interface PatternProgress {
  /** 0-based index of the leg being run now, or legs.length when finished. */
  currentLegIndex: number;
  /** Seconds elapsed on the current leg. */
  elapsedInLegSec: number;
  /** Seconds left before the turn onto the next leg. */
  remainingInLegSec: number;
  /** True once every leg has been run. */
  done: boolean;
}

/**
 * Where the boat is in the pattern, given when it started and the time now. We
 * walk the legs, subtracting each leg's run time, so the countdown always points
 * at the next turn. Pure, so the UI just re-reads it on a one-second tick.
 */
export function patternProgress(
  legs: SearchLeg[],
  startedAtMs: number,
  nowMs: number,
): PatternProgress {
  let remaining = Math.max(0, (nowMs - startedAtMs) / 1000);
  for (let i = 0; i < legs.length; i++) {
    const legSec = legs[i].timeSec;
    if (!Number.isFinite(legSec)) {
      return { currentLegIndex: i, elapsedInLegSec: remaining, remainingInLegSec: Infinity, done: false };
    }
    if (remaining < legSec) {
      return {
        currentLegIndex: i,
        elapsedInLegSec: remaining,
        remainingInLegSec: legSec - remaining,
        done: false,
      };
    }
    remaining -= legSec;
  }
  return { currentLegIndex: legs.length, elapsedInLegSec: 0, remainingInLegSec: 0, done: true };
}

/** Format seconds as mm:ss (for the leg time and the countdown). */
export function formatLegTime(sec: number): string {
  if (!Number.isFinite(sec)) return '—';
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}
